import {
  BellOutlined,
  CheckCircleOutlined,
  PauseCircleFilled,
  PlayCircleFilled,
  SettingFilled,
  SettingOutlined,
} from '@ant-design/icons'
import { Button, Empty, List, Select, Spin, Switch, Tabs, Tooltip, Typography, message } from 'antd'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import styled from 'styled-components'
import dayjs from '../../../../lib/utils/configured-dayjs'
import { useNotifications } from './context'
import {
  NotificationSoundPlayback,
  initNotificationAudioContext,
  playNotificationSound,
} from './sound'
import {
  NotificationItem,
  NotificationSetting,
  NotificationSoundKey,
  NotificationStatus,
  NotificationType,
} from './types'

const { Text } = Typography
const PAGE_SIZE = 20
const LOAD_MORE_OFFSET = 96
const SYSTEM_BUTTON_COLOR = 'var(--system-button, var(--system-primary, #1677ff))'

const PanelWrapper = styled.div`
  width: 420px;
  max-width: calc(100vw - 32px);
`

const PanelHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  padding: 2px 0 12px;
`

const HeaderTitle = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`

const ListWrapper = styled.div`
  max-height: min(560px, calc(100vh - 180px));
  overflow-y: auto;
  padding-right: 2px;
`

const NotificationRow = styled.button<{ $unread: boolean }>`
  width: 100%;
  border: 0;
  border-radius: 8px;
  background: ${({ $unread }) => ($unread ? '#f2f7ff' : '#fff')};
  padding: 12px;
  text-align: left;
  cursor: pointer;
  transition: background 0.2s, box-shadow 0.2s;

  &:hover {
    background: #f6f8fa;
    box-shadow: inset 0 0 0 1px #e6f0ff;
  }
`

const RowTop = styled.div`
  display: flex;
  gap: 10px;
`

const RowTitle = styled.div`
  min-width: 0;
  display: flex;
  gap: 8px;
  align-items: center;
`

const TitleText = styled(Text)`
  max-width: 250px;
`

const RowMessage = styled(Text)`
  display: block;
  margin: 6px 0 8px;
`

const SettingsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  max-height: min(560px, calc(100vh - 180px));
  overflow-y: auto;
`

const SettingsGroup = styled.div`
  border: 1px solid #edf0f5;
  border-radius: 8px;
  background: #fff;
  overflow: hidden;
`

const SettingsGroupTitle = styled.div`
  padding: 10px 12px;
  background: #f8fafc;
  border-bottom: 1px solid #edf0f5;
`

const SoundSettingsHeader = styled(SettingsGroupTitle)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
`

const SoundPickerBody = styled.div`
  padding: 10px 12px 12px;
`

const SoundOptionContent = styled.div`
  min-width: 0;
  display: grid;
  grid-template-columns: minmax(0, 1fr) 28px;
  gap: 8px;
  align-items: center;
`

const SoundOptionPreviewButton = styled.button<{ $active: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  padding: 0;
  border: 0;
  border-radius: 4px;
  background: ${({ $active }) =>
    $active ? `color-mix(in srgb, ${SYSTEM_BUTTON_COLOR} 10%, transparent)` : 'transparent'};
  color: ${({ $active }) => ($active ? SYSTEM_BUTTON_COLOR : '#4f5964')};
  cursor: pointer;
  transition: color 0.2s, border-color 0.2s, background-color 0.2s;

  &:hover {
    color: ${SYSTEM_BUTTON_COLOR};
    background: color-mix(in srgb, ${SYSTEM_BUTTON_COLOR} 8%, #fff);
  }

  &:focus-visible {
    outline: 2px solid var(--system-focus, ${SYSTEM_BUTTON_COLOR});
    outline-offset: 2px;
  }

  svg {
    width: 17px;
    height: 17px;
  }
`

const SettingsRow = styled.div`
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 12px;
  align-items: center;
  padding: 12px;
  border-bottom: 1px solid #edf0f5;

  &:last-child {
    border-bottom: 0;
  }
`

const BottomLoader = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 8px;
  padding: 12px 0 4px;
`

const SettingsToggle = styled.button<{ $active: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 0;
  border: 0;
  background: transparent;
  color: #1f2933;
  cursor: pointer;
  font: inherit;

  &:hover .settings-icon-outline {
    display: none;
  }

  &:hover .settings-icon-filled {
    display: inline-flex;
  }
`

const SettingsToggleIcon = styled.span<{ $active: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  color: ${({ $active }) => ($active ? SYSTEM_BUTTON_COLOR : '#5c6670')};
  transition: color 0.2s;

  .settings-icon-outline {
    display: ${({ $active }) => ($active ? 'none' : 'inline-flex')};
  }

  .settings-icon-filled {
    display: ${({ $active }) => ($active ? 'inline-flex' : 'none')};
    color: ${SYSTEM_BUTTON_COLOR};
  }
`

const isUnread = (item: NotificationItem) => !item.read_at

interface NotificationCenterPanelProps {
  onClose: () => void
}

export const NotificationCenterPanel = ({ onClose }: NotificationCenterPanelProps) => {
  const navigate = useNavigate()
  const [activeTab, setActiveTab] = useState<'all' | 'unread'>('all')
  const [isSettingsOpen, setSettingsOpen] = useState(false)
  const [loadingMore, setLoadingMore] = useState(false)
  const [previewingSoundKey, setPreviewingSoundKey] = useState<NotificationSoundKey | null>(null)
  const previewAudioContextRef = useRef<AudioContext | null>(null)
  const previewPlaybackRef = useRef<NotificationSoundPlayback | null>(null)
  const previewTimerRef = useRef<number | null>(null)
  const previewRequestRef = useRef(0)
  const {
    items,
    unreadCount,
    hasMore,
    loading,
    settings,
    soundKey,
    soundOptions,
    settingsLoading,
    activeStatus,
    loadNotifications,
    markAsRead,
    markAllAsRead,
    updateSettings,
    updateSound,
  } = useNotifications()

  const stopSoundPreview = useCallback(() => {
    previewRequestRef.current += 1
    previewPlaybackRef.current?.stop()
    previewPlaybackRef.current = null
    if (previewTimerRef.current) {
      window.clearTimeout(previewTimerRef.current)
      previewTimerRef.current = null
    }
    setPreviewingSoundKey(null)
  }, [])

  const playSoundPreview = useCallback(
    async (nextSoundKey: NotificationSoundKey) => {
      if (previewingSoundKey === nextSoundKey) {
        stopSoundPreview()
        return
      }

      stopSoundPreview()
      initNotificationAudioContext(previewAudioContextRef)
      const requestId = previewRequestRef.current

      try {
        const playback = await playNotificationSound(previewAudioContextRef, nextSoundKey)
        if (!playback || requestId !== previewRequestRef.current) {
          playback?.stop()
          return
        }

        previewPlaybackRef.current = playback
        setPreviewingSoundKey(nextSoundKey)
        previewTimerRef.current = window.setTimeout(() => {
          previewPlaybackRef.current = null
          previewTimerRef.current = null
          setPreviewingSoundKey(null)
        }, (playback.duration + 0.12) * 1000)
      } catch (err) {
        console.error('[Notifications] Sound preview error:', err)
        message.error('Не удалось воспроизвести звук')
      }
    },
    [previewingSoundKey, stopSoundPreview],
  )

  const handleSoundChange = useCallback(
    (nextSoundKey: NotificationSoundKey) => {
      playSoundPreview(nextSoundKey)
      updateSound(nextSoundKey).catch(err => {
        console.error('[Notifications] Sound save error:', err)
        message.error('Не удалось сохранить выбранный звук')
      })
    },
    [playSoundPreview, updateSound],
  )

  useEffect(
    () => () => {
      previewRequestRef.current += 1
      previewPlaybackRef.current?.stop()
      if (previewTimerRef.current) {
        window.clearTimeout(previewTimerRef.current)
      }
      previewAudioContextRef.current?.close().catch(() => undefined)
    },
    [],
  )

  const handleOpenNotification = useCallback(
    async (item: NotificationItem) => {
      try {
        await markAsRead(item.id)
      } catch (err) {
        message.error('Не удалось отметить уведомление прочитанным')
        return
      }

      if (item.url) {
        navigate(item.url)
        onClose()
      }
    },
    [markAsRead, navigate, onClose],
  )

  const handleStatusChange = useCallback(
    (tab: string) => {
      const nextTab = tab as 'all' | 'unread'
      setActiveTab(nextTab)
      setSettingsOpen(false)
      loadNotifications(nextTab as NotificationStatus).catch(err => {
        console.error('[Notifications] Load tab error:', err)
        message.error('Не удалось загрузить уведомления')
      })
    },
    [loadNotifications],
  )

  const handleTabClick = useCallback(
    (tab: string) => {
      if (isSettingsOpen && tab === activeTab) {
        setSettingsOpen(false)
      }
    },
    [activeTab, isSettingsOpen],
  )

  const handleReadAll = useCallback(() => {
    markAllAsRead().catch(err => {
      console.error('[Notifications] Read all error:', err)
      message.error('Не удалось отметить уведомления прочитанными')
    })
  }, [markAllAsRead])

  const handleListScroll = useCallback(
    (event: React.UIEvent<HTMLDivElement>) => {
      const target = event.currentTarget
      const distanceToBottom = target.scrollHeight - target.scrollTop - target.clientHeight

      if (!hasMore || loading || loadingMore || distanceToBottom > LOAD_MORE_OFFSET) {
        return
      }

      setLoadingMore(true)
      const nextPage = Math.floor(items.length / PAGE_SIZE) + 1
      loadNotifications(activeStatus, nextPage, true)
        .catch(err => {
          console.error('[Notifications] Load more error:', err)
          message.error('Не удалось загрузить ещё уведомления')
        })
        .finally(() => {
          setLoadingMore(false)
        })
    },
    [activeStatus, hasMore, items.length, loadNotifications, loading, loadingMore],
  )

  const handleSettingChange = useCallback(
    (type: NotificationType, patch: Partial<NotificationSetting>) => {
      const soundEligibleSettings = settings.filter(
        item => item.role_enabled && item.role_sound_enabled && item.enabled,
      )
      const globalSoundEnabled =
        soundEligibleSettings.length > 0 && soundEligibleSettings.every(item => item.sound_enabled)

      const nextSettings = settings.map(item => {
        if (item.type !== type) {
          return item
        }
        const next = {
          ...item,
          ...patch,
          sound_enabled:
            patch.enabled === true && patch.sound_enabled === undefined
              ? globalSoundEnabled
              : patch.sound_enabled ?? item.sound_enabled,
        }
        return {
          ...next,
          enabled: item.role_enabled && next.enabled,
          sound_enabled: item.role_sound_enabled && next.enabled && next.sound_enabled,
        }
      })

      updateSettings(nextSettings).catch(err => {
        console.error('[Notifications] Settings save error:', err)
        message.error('Не удалось сохранить настройки')
      })
    },
    [settings, updateSettings],
  )

  const handleGlobalSoundChange = useCallback(
    (checked: boolean) => {
      const nextSettings = settings.map(item => ({
        ...item,
        sound_enabled: Boolean(
          checked && item.role_enabled && item.role_sound_enabled && item.enabled,
        ),
      }))

      updateSettings(nextSettings).catch(err => {
        console.error('[Notifications] Settings save error:', err)
        message.error('Не удалось сохранить настройки')
      })
    },
    [settings, updateSettings],
  )

  const renderNotifications = () => (
    <>
      <PanelHeader>
        <HeaderTitle>
          <Text strong>Уведомления</Text>
          <Text type="secondary">
            {unreadCount ? `Непрочитанных: ${unreadCount}` : 'Новых нет'}
          </Text>
        </HeaderTitle>
        <Button
          size="small"
          icon={<CheckCircleOutlined />}
          disabled={!unreadCount}
          onClick={handleReadAll}
        >
          Отметить все
        </Button>
      </PanelHeader>
      <Spin spinning={loading && !loadingMore}>
        <ListWrapper onScroll={handleListScroll}>
          {!items.length ? (
            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Уведомлений нет" />
          ) : (
            <List
              dataSource={items}
              split={false}
              renderItem={item => (
                <List.Item style={{ padding: '0 0 8px' }}>
                  <NotificationRow
                    $unread={isUnread(item)}
                    onClick={() => handleOpenNotification(item)}
                  >
                    <RowTop>
                      <RowTitle>
                        <BellOutlined style={{ color: '#1677ff' }} />
                        <TitleText strong={isUnread(item)} ellipsis>
                          {item.title}
                        </TitleText>
                      </RowTitle>
                    </RowTop>
                    <RowMessage type="secondary">{item.message}</RowMessage>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      {dayjs(item.created_at).fromNow()}
                    </Text>
                  </NotificationRow>
                </List.Item>
              )}
            />
          )}
          {loadingMore && (
            <BottomLoader>
              <Spin size="small" />
              <Text type="secondary">Загружаем...</Text>
            </BottomLoader>
          )}
        </ListWrapper>
      </Spin>
    </>
  )

  const renderSettings = () => {
    const availableSettings = settings.filter(item => item.role_enabled)
    const soundEligibleSettings = availableSettings.filter(
      item => item.role_enabled && item.role_sound_enabled && item.enabled,
    )
    const globalSoundAvailable = soundEligibleSettings.length > 0
    const globalSoundEnabled =
      soundEligibleSettings.length > 0 && soundEligibleSettings.every(item => item.sound_enabled)

    return (
      <Spin spinning={settingsLoading}>
        <SettingsList>
          <SettingsGroup>
            <SoundSettingsHeader>
              <Text strong>Звук уведомлений</Text>
              <Switch
                checked={globalSoundEnabled}
                disabled={!globalSoundAvailable}
                onChange={handleGlobalSoundChange}
              />
            </SoundSettingsHeader>
            <SoundPickerBody>
              <Select<NotificationSoundKey>
                value={soundKey}
                options={soundOptions.map(option => ({
                  value: option.key,
                  label: option.label,
                }))}
                optionRender={option => {
                  const optionKey = option.value as NotificationSoundKey
                  const isPreviewing = previewingSoundKey === optionKey
                  return (
                    <SoundOptionContent>
                      <span>{option.label}</span>
                      <SoundOptionPreviewButton
                        type="button"
                        $active={isPreviewing}
                        title={isPreviewing ? 'Остановить' : 'Прослушать'}
                        aria-label={`${isPreviewing ? 'Остановить' : 'Прослушать'} ${option.label}`}
                        onPointerDown={event => {
                          event.preventDefault()
                          event.stopPropagation()
                          playSoundPreview(optionKey)
                        }}
                        onClick={event => {
                          event.preventDefault()
                          event.stopPropagation()
                          if (event.detail === 0) {
                            playSoundPreview(optionKey)
                          }
                        }}
                      >
                        {isPreviewing ? <PauseCircleFilled /> : <PlayCircleFilled />}
                      </SoundOptionPreviewButton>
                    </SoundOptionContent>
                  )
                }}
                onChange={handleSoundChange}
                aria-label="Мелодия уведомлений"
                style={{ width: '100%' }}
                popupMatchSelectWidth
              />
            </SoundPickerBody>
          </SettingsGroup>
          <SettingsGroup>
            <SettingsGroupTitle>
              <Text strong>Типы уведомлений</Text>
            </SettingsGroupTitle>
            {availableSettings.map(item => {
              return (
                <SettingsRow key={item.type}>
                  <div>
                    <Text strong>{item.label}</Text>
                  </div>
                  <Switch
                    checked={item.enabled}
                    onChange={checked =>
                      handleSettingChange(item.type, {
                        enabled: checked,
                        sound_enabled: checked ? globalSoundEnabled : false,
                      })
                    }
                  />
                </SettingsRow>
              )
            })}
          </SettingsGroup>
        </SettingsList>
      </Spin>
    )
  }

  const settingsButton = (
    <Tooltip title="Настройки">
      <SettingsToggle
        type="button"
        $active={isSettingsOpen}
        onClick={() => setSettingsOpen(prev => !prev)}
      >
        <SettingsToggleIcon $active={isSettingsOpen}>
          <SettingOutlined className="settings-icon-outline" />
          <SettingFilled className="settings-icon-filled" />
        </SettingsToggleIcon>
        Настройки
      </SettingsToggle>
    </Tooltip>
  )

  return (
    <PanelWrapper>
      <Tabs
        activeKey={activeTab}
        onChange={handleStatusChange}
        onTabClick={handleTabClick}
        tabBarExtraContent={settingsButton}
      >
        <Tabs.TabPane tab="Все" key="all">
          {isSettingsOpen ? renderSettings() : renderNotifications()}
        </Tabs.TabPane>
        <Tabs.TabPane tab="Непрочитанные" key="unread">
          {isSettingsOpen ? renderSettings() : renderNotifications()}
        </Tabs.TabPane>
      </Tabs>
    </PanelWrapper>
  )
}
